import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PREDICTION_FIELDS, todayISO } from "@/lib/constants";
import { formatUKDate } from "@/lib/date";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useScopedPath } from "@/lib/scoped";
import ProLeagueBanner from "@/components/ProLeagueBanner";
import LeagueEntryGate from "@/components/LeagueEntryGate";
import GameweekBanner from "@/components/GameweekBanner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ChevronRight, CheckCircle2, Lock, Copy, Share2 } from "lucide-react";

function pointsFor(pred, actual, fieldKey) {
  if (pred == null || actual == null) return 0;
  const diff = Math.abs(parseInt(pred, 10) - parseInt(actual, 10));
  if (fieldKey === "throw_ins") return diff <= 5 ? 2 : 0;
  if (diff === 0) return 3;
  return diff === 1 ? 1 : 0;
}

/**
 * iter107 — parse `<match_date>T<kickoff>` as the app's configured
 * match timezone (Europe/London) and return the epoch ms of kickoff.
 * Returns Infinity for malformed inputs so the client caller reads
 * "predictions closed" (mirrors the backend `kickoff_locks_predictions`
 * fail-closed policy).
 *
 * Notes on TZ: we can't ask the browser to parse a naive datetime as
 * a foreign TZ. So we compare epoch millis instead — take the naive
 * UK datetime, ask JS what UTC time it is when the naive time is UK,
 * using Intl.DateTimeFormat's offset for Europe/London. Simpler
 * approximation used here: parse `${date}T${time}` as if it were
 * local, then adjust by the difference between the browser's TZ and
 * London. For a UK user (99% of Dugsy players) the browser IS London
 * time — the offset delta is 0 and the naive parse is exact. For a
 * non-UK user the delta is applied.
 */
function kickoffEpochUk(match_date, kickoff) {
  if (!match_date || !kickoff) return Infinity;
  const ko = String(kickoff).trim();
  const iso = ko.length === 5 ? `${match_date}T${ko}:00` : `${match_date}T${ko}`;
  const asIfLocal = new Date(iso);
  if (Number.isNaN(asIfLocal.getTime())) return Infinity;
  // Offset delta: browser vs London.
  const londonNow = new Date().toLocaleString("en-US", { timeZone: "Europe/London" });
  const browserNow = new Date().toLocaleString("en-US", { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });
  const deltaMs = new Date(browserNow).getTime() - new Date(londonNow).getTime();
  return asIfLocal.getTime() + deltaMs;
}

function getStatusBadge(isFinished, hasPrediction, kickoffPassed) {
  if (isFinished) return { label: "FINISHED", className: "bg-zinc-950 text-white hover:bg-zinc-950" };
  if (hasPrediction) return { label: "LOCKED", className: "bg-zinc-900 text-white hover:bg-zinc-900" };
  if (kickoffPassed) return { label: "CLOSED", className: "bg-zinc-500 text-white hover:bg-zinc-500" };
  return { label: "OPEN", className: "bg-[#0038FF] text-white hover:bg-[#0038FF]" };
}

function getPointsPillClass(value) {
  if (value === 3) return "bg-[#0038FF] text-white";
  if (value === 2) return "bg-emerald-600 text-white";
  if (value === 1) return "bg-zinc-950 text-white";
  return "bg-zinc-100 text-zinc-500";
}

function PointsPill({ value, dim }) {
  if (dim) {
    return <span className="inline-flex items-center justify-center min-w-[2.5rem] h-7 px-2 rounded-sm text-zinc-300 text-sm tabular-nums">—</span>;
  }
  return (
    <span className={`inline-flex items-center justify-center min-w-[2.5rem] h-7 px-2 rounded-sm font-bold text-sm tabular-nums ${getPointsPillClass(value)}`}>
      {value}
    </span>
  );
}

function getCardFooterText(locked, isFinished, actual, totalPoints, kickoffPassed) {
  if (locked && isFinished) return `Final · Total ${totalPoints} pts`;
  if (locked && actual) return "Partial result · awaiting full time";
  if (locked) return "Submitted · awaiting result";
  if (isFinished) return "Closed — you didn't submit";
  if (kickoffPassed) return "Predictions closed — match has kicked off";
  return "Not submitted";
}

function MatchCard({ match, onSaved, locked: paymentLocked = false }) {
  const scope = useScopedPath();
  const hasPrediction = Boolean(match.my_prediction);
  const isFinished = match.status === "finished";
  // iter107 — client-side live tick so the UI closes predictions the
  // moment kickoff arrives, without a page refresh. The server flag
  // stays authoritative (`match.kickoff_passed` from the JSON), and
  // the epoch fallback covers the "user opened the page pre-kickoff
  // and lingered past it" scenario. `Date.now()` is TZ-agnostic
  // millisecond epoch — we compare it to a UK-aligned kickoff epoch
  // computed in `kickoffEpochUk`. The `nowMs` state re-renders every
  // 15s to force a re-evaluation of this flag as time passes.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);
  const koEpoch = kickoffEpochUk(match.match_date, match.kickoff);
  const kickoffPassed = Boolean(match.kickoff_passed) || nowMs >= koEpoch;
  const locked = hasPrediction; // once submitted, never editable
  const formDisabled = locked || isFinished || kickoffPassed || paymentLocked;
  const actual = match.actual || null;

  const [values, setValues] = useState({
    goals: match.my_prediction?.goals ?? "",
    shots_on_target: match.my_prediction?.shots_on_target ?? "",
    corners: match.my_prediction?.corners ?? "",
    offsides: match.my_prediction?.offsides ?? "",
    throw_ins: match.my_prediction?.throw_ins ?? "",
    fouls: match.my_prediction?.fouls ?? "",
  });
  const [saving, setSaving] = useState(false);
  const inputRefs = useRef([]);
  const submitBtnRef = useRef(null);

  // Sanitize to digits only, max 2 chars (predictions are 0–99).
  const sanitize = (raw) => String(raw ?? "").replace(/\D/g, "").slice(0, 2);

  const focusIndex = (idx) => {
    const next = inputRefs.current[idx];
    if (next && typeof next.focus === "function") {
      next.focus();
      // For text-like inputs we can also select to allow quick overwrite
      if (typeof next.select === "function") next.select();
    } else if (idx >= PREDICTION_FIELDS.length && submitBtnRef.current) {
      submitBtnRef.current.focus();
    }
  };

  const handleFieldChange = (idx, key, raw) => {
    const v = sanitize(raw);
    setValues((s) => ({ ...s, [key]: v }));
    // Auto-advance once the user has entered the max digits (2)
    if (v.length === 2) focusIndex(idx + 1);
  };

  const handleFieldKeyDown = (idx, key, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // Treat Enter as "I'm done with this field" — advance regardless of digit count
      if (values[key] !== "" && values[key] != null) {
        focusIndex(idx + 1);
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    // iter107 — final client-side re-check right before the network
    // call. Belt-and-braces alongside the useEffect ticker: a user
    // could press Submit on the exact tick that kickoff arrives, so
    // we recompute against `Date.now()` here too. The backend gate
    // (`kickoff_locks_predictions`) still enforces atomically.
    if (Date.now() >= kickoffEpochUk(match.match_date, match.kickoff)) {
      toast.error("Predictions are closed — this match has already kicked off.");
      return;
    }
    const payload = {};
    for (const f of PREDICTION_FIELDS) {
      const n = parseInt(values[f.key], 10);
      if (Number.isNaN(n) || n < 0) {
        toast.error(`Enter a valid number for ${f.label}`);
        return;
      }
      payload[f.key] = n;
    }
    if (!window.confirm("Predictions cannot be changed once submitted. Submit now?")) return;
    setSaving(true);
    try {
      await api.post(`/matches/${match.id}/predict`, payload);
      toast.success("Prediction locked in");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <article
      data-testid={`match-card-${match.id}`}
      className="border border-zinc-200 rounded-sm bg-white"
    >
      <header className="px-6 py-5 border-b border-zinc-200 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500" data-testid={`match-kickoff-${match.id}`}>
              {match.kickoff}
              {match.match_date && (
                <span className="ml-2 text-zinc-400" data-testid={`match-date-${match.id}`}>
                  · {formatUKDate(match.match_date, { weekday: "short", day: "numeric", month: "short" })}
                </span>
              )}
            </span>
            {(() => {
              const b = getStatusBadge(isFinished, hasPrediction, kickoffPassed);
              return (
                <Badge className={`rounded-sm text-[10px] tracking-widest ${b.className}`}>
                  {b.label}
                </Badge>
              );
            })()}
          </div>
          <h2 className="mt-1 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">
            {match.home_team} <span className="text-zinc-300">vs</span> {match.away_team}
          </h2>
        </div>
        {isFinished && (
          <Link
            to={scope(`/matches/${match.id}`)}
            data-testid={`view-results-${match.id}`}
            className="inline-flex items-center gap-1 text-sm font-semibold text-[#0038FF] hover:underline whitespace-nowrap"
          >
            League scores <ChevronRight className="h-4 w-4" />
          </Link>
        )}
      </header>

      <form onSubmit={submit} className="p-6">
        {/* Header instruction */}
        {!locked && !kickoffPassed && !isFinished && (
          <p className="text-sm text-zinc-500 mb-4">
            Enter combined match totals (both teams together). Fields auto-advance as you type — press <kbd className="px-1.5 py-0.5 rounded-sm border border-zinc-300 bg-zinc-50 text-[11px] font-semibold text-zinc-700">Enter</kbd> on a single-digit value to jump to the next. One submission per match — no edits after.
          </p>
        )}
        {!locked && kickoffPassed && !isFinished && (
          <p className="text-sm text-zinc-500 mb-4" data-testid={`closed-notice-${match.id}`}>
            Predictions are closed — this match has already kicked off.
          </p>
        )}

        {/* Three-row grid: Prediction / Actual / Points */}
        <div className="overflow-x-auto -mx-6 sm:mx-0 px-6 sm:px-0">
          <div className="min-w-[720px]">
            {/* Column headers */}
            <div
              className="grid gap-3 items-end pb-3 border-b border-zinc-200"
              style={{ gridTemplateColumns: `120px repeat(${PREDICTION_FIELDS.length}, minmax(0, 1fr))` }}
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">&nbsp;</div>
              {PREDICTION_FIELDS.map((f) => (
                <div key={f.key} className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 text-center">
                  {f.label}
                </div>
              ))}
            </div>

            {/* Row 1: Predictions */}
            <div
              className="grid gap-3 items-center py-3 border-b border-zinc-100"
              style={{ gridTemplateColumns: `120px repeat(${PREDICTION_FIELDS.length}, minmax(0, 1fr))` }}
            >
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-950">
                Prediction
              </Label>
              {PREDICTION_FIELDS.map((f, idx) => {
                if (locked) {
                  return (
                    <div
                      key={f.key}
                      data-testid={`predict-${f.key}-${match.id}`}
                      className="h-11 flex items-center justify-center rounded-sm bg-zinc-50 border border-zinc-200 text-base font-semibold tabular-nums text-zinc-950 relative"
                      aria-label={`${f.label} (locked)`}
                    >
                      {values[f.key]}
                      <Lock className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-400" aria-hidden="true" />
                    </div>
                  );
                }
                return (
                  <Input
                    key={f.key}
                    id={`${match.id}-${f.key}`}
                    ref={(el) => { inputRefs.current[idx] = el; }}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={2}
                    autoComplete="off"
                    disabled={formDisabled}
                    required={!formDisabled}
                    value={values[f.key]}
                    onChange={(e) => handleFieldChange(idx, f.key, e.target.value)}
                    onKeyDown={(e) => handleFieldKeyDown(idx, f.key, e)}
                    onFocus={(e) => e.target.select?.()}
                    data-testid={`predict-${f.key}-${match.id}`}
                    aria-label={`Prediction ${f.label}`}
                    className="h-11 rounded-sm border-zinc-300 text-base font-semibold text-center focus-visible:ring-[#0038FF] disabled:bg-zinc-50 disabled:text-zinc-950 disabled:opacity-100"
                  />
                );
              })}
            </div>

            {/* Progressive disclosure: Actual + Points rows only after admin finalises */}
            <div
              data-testid={`results-disclosure-${match.id}`}
              className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${
                actual ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              }`}
              aria-hidden={!actual}
            >
              <div className="overflow-hidden">
                {/* Row 2: Actual */}
                <div
                  className="grid gap-3 items-center py-3 border-b border-zinc-100"
                  style={{ gridTemplateColumns: `120px repeat(${PREDICTION_FIELDS.length}, minmax(0, 1fr))` }}
                  data-testid={`actual-row-${match.id}`}
                >
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
                    Actual
                  </Label>
                  {PREDICTION_FIELDS.map((f) => (
                    <div
                      key={f.key}
                      data-testid={`actual-${f.key}-${match.id}`}
                      className="h-11 flex items-center justify-center text-base font-semibold tabular-nums rounded-sm border border-dashed border-zinc-200 bg-white"
                    >
                      {actual ? actual[f.key] : <span className="text-zinc-300">—</span>}
                    </div>
                  ))}
                </div>

                {/* Row 3: Points */}
                <div
                  className="grid gap-3 items-center py-3"
                  style={{ gridTemplateColumns: `120px repeat(${PREDICTION_FIELDS.length}, minmax(0, 1fr))` }}
                  data-testid={`points-row-${match.id}`}
                >
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
                    Points
                  </Label>
                  {PREDICTION_FIELDS.map((f) => {
                    const p = match.my_prediction?.[f.key];
                    const a = actual?.[f.key];
                    const ready = match.my_prediction != null && actual != null;
                    const pts = ready ? pointsFor(p, a, f.key) : 0;
                    return (
                      <div key={f.key} className="flex items-center justify-center" data-testid={`points-${f.key}-${match.id}`}>
                        <PointsPill value={pts} dim={!ready} />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer area */}
        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          {(() => {
            if (paymentLocked && !locked && !isFinished && !kickoffPassed) {
              return (
                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-amber-700">
                  <Lock className="h-3.5 w-3.5" />
                  Pay to unlock — see banner above
                </span>
              );
            }
            const totalPoints =
              match.my_prediction?.points_total ??
              PREDICTION_FIELDS.reduce(
                (s, f) => s + pointsFor(match.my_prediction?.[f.key], actual?.[f.key], f.key),
                0,
              );
            const footerText = getCardFooterText(locked, isFinished, actual, totalPoints, kickoffPassed);
            const Icon = locked || isFinished || kickoffPassed ? Lock : null;
            return (
              <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-zinc-500">
                {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
                {footerText}
              </span>
            );
          })()}

          {!locked && !isFinished && !kickoffPassed && !paymentLocked && (
            <Button
              type="submit"
              ref={submitBtnRef}
              disabled={saving}
              data-testid={`save-prediction-${match.id}`}
              className="h-11 px-6 rounded-sm bg-[#0038FF] hover:bg-[#002BE6] text-white font-semibold tracking-tight active:scale-[0.98] transition-colors"
            >
              {saving ? "Submitting…" : "Submit prediction"}
            </Button>
          )}
          {locked && (
            <CheckCircle2 className="h-5 w-5 text-[#0038FF]" aria-hidden="true" />
          )}
        </div>
      </form>
    </article>
  );
}

function InviteFriendCard({ inviterName, brandName, brandInviteText }) {
  const scope = useScopedPath();
  // Personal invite links land recipients directly on the branded Register
  // page (`/c/[slug]/register?ref=<inviter>` inside a client env,
  // `/register?ref=<inviter>` on the global platform) so they see a warm
  // "<inviter> wants you to join <brand>" banner and can sign up in one
  // step. Returning users can still tap "Sign in" from that same page.
  const refParam = inviterName ? `?ref=${encodeURIComponent(inviterName)}` : "";
  const inviteUrl = `${window.location.origin}${scope("/register")}${refParam}`;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the link to copy manually");
    }
  };

  // iter175 — bespoke per-pub WhatsApp invite. The pub's editor writes
  // the copy under /c/[slug]/editor > "Invite a friend WhatsApp text";
  // we render it verbatim after substituting {user}, {pub_name} / {pub
  // name} (both forms accepted for admin ergonomics) and {link}. If the
  // pub hasn't set anything, fall back to the platform default the
  // product owner specified.
  const target = brandName || "Dugsy's Football Challenge";
  const senderName = inviterName || "A friend";
  const DEFAULT_INVITE =
    "{user} invited you to play {pub_name} Football Challenge! Sign up at {link}";
  const rawTemplate = (brandInviteText && brandInviteText.trim()) || DEFAULT_INVITE;
  const shareMessage = rawTemplate
    .split("{user}").join(senderName)
    .split("{pub_name}").join(target)
    .split("{pub name}").join(target)
    .split("{link}").join(inviteUrl);
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;

  return (
    <aside
      data-testid="invite-friend-card"
      className="w-full sm:w-auto sm:max-w-xs rounded-2xl border border-zinc-200 bg-zinc-50 p-4"
    >
      <p className="font-heading text-xl sm:text-2xl font-semibold text-zinc-950">
        Invite a friend
      </p>
      <div className="mt-3 flex gap-2">
        <Button
          type="button"
          onClick={handleCopy}
          data-testid="invite-copy-button"
          variant="outline"
          className="flex-1 h-10 px-3 text-sm gap-1.5"
        >
          <Copy className="h-3.5 w-3.5" />
          {copied ? "Copied" : "Copy link"}
        </Button>
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="invite-whatsapp-button"
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 px-3 rounded-md border border-zinc-300 bg-white text-sm font-medium hover:bg-zinc-100"
        >
          <Share2 className="h-3.5 w-3.5" />
          WhatsApp
        </a>
      </div>
    </aside>
  );
}

export default function MatchesPage() {
  const { user } = useAuth();
  const { brand } = useBrand();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [matchDayAccess, setMatchDayAccess] = useState(true);
  const today = todayISO();
  // Backend `/matches/upcoming` resolves to today OR the next date in the
  // active season that actually has fixtures. Keep both around so the
  // ProLeagueBanner / MatchDayPaymentGate lock against the *resolved* date
  // (not stale `today`).
  const [resolvedDate, setResolvedDate] = useState(today);
  const [isToday, setIsToday] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/matches/upcoming`);
      setMatches(Array.isArray(data?.matches) ? data.matches : []);
      setResolvedDate(data?.date || today);
      setIsToday(Boolean(data?.is_today));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [today]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/predictions/my-history`);
      setHistory(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadHistory();
  }, [load, loadHistory]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500" data-testid="matchday-date-label">
              {formatUKDate(resolvedDate, { weekday: "long", month: "long", day: "numeric" })}
              {!isToday && matches.length > 0 && (
                <span className="ml-2 text-amber-700 normal-case tracking-normal font-semibold" data-testid="next-matchday-badge">
                  · Next matchday
                </span>
              )}
            </p>
            {user?.name && (
              <p
                data-testid="welcome-back-greeting"
                className="font-heading text-2xl sm:text-3xl font-semibold text-zinc-500 mt-2"
              >
                Welcome back, {user.name.split(" ")[0]}.
              </p>
            )}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-950 mt-1">
              Upcoming predictions.
            </h1>
            {!isToday && matches.length > 0 && (
              <p className="mt-3 text-sm text-zinc-500" data-testid="matchday-fallback-hint">
                No fixtures today — showing the next matchday.
              </p>
            )}
            <p className="mt-3 text-base text-zinc-600 max-w-2xl" data-testid="predictions-scoring-hint">
              Score <strong>3 points</strong> for an exact prediction on goals, shots on target, corners,
              offsides or fouls, and <strong>1 point</strong> if you&apos;re within +/-1 of the actual result. For
              throw-ins, score <strong>2 points</strong> for any prediction within +/-5 of the actual
              result (including the exact figure).
            </p>
          </div>
          <InviteFriendCard
            inviterName={user?.name || ""}
            brandName={brand?.name || ""}
            brandInviteText={brand?.whatsapp_invite_text || ""}
          />
        </div>

        {/* iter143 — gameweek chip sits to the LEFT of the ProLeagueBanner
            (prize banner) per user layout spec, at the SAME HEIGHT (both
            children stretch to the tallest sibling via items-stretch).
            The `[&_section]:mb-0` override strips ProLeagueBanner's own
            `mb-10` inside this flex row so the row height matches the
            visible prize banner (was inflating pill to 136px). Bottom
            spacing to next element is handled by the row's own `mb-6`.
            Flex-wraps to stacked on mobile. */}
        <div className="mb-6 flex flex-wrap items-stretch gap-3">
          <GameweekBanner variant="pill" />
          <div className="flex-1 min-w-[240px] flex [&_section]:mb-0">
            <div className="flex-1"><ProLeagueBanner matchDate={resolvedDate} /></div>
          </div>
        </div>

        <LeagueEntryGate onAccessChange={setMatchDayAccess} />

        {loading ? (
          <div className="text-sm uppercase tracking-[0.2em] text-zinc-500">Loading…</div>
        ) : matches.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-12 sm:p-16 text-center" data-testid="no-matches">
            <div className="mx-auto mb-6 w-24 h-24 flex items-center justify-center rounded-full bg-zinc-50">
              <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" className="w-12 h-12 text-zinc-400" aria-hidden="true">
                <circle cx="32" cy="32" r="22" />
                <path d="M32 10 L40 22 L32 32 L24 22 Z" fill="currentColor" stroke="none" opacity="0.5" />
                <path d="M32 54 L40 42 L32 32 L24 42 Z" fill="currentColor" stroke="none" opacity="0.5" />
                <path d="M10 32 L22 24 L32 32 L22 40 Z" fill="currentColor" stroke="none" opacity="0.35" />
                <path d="M54 32 L42 24 L32 32 L42 40 Z" fill="currentColor" stroke="none" opacity="0.35" />
              </svg>
            </div>
            <p className="font-heading text-2xl sm:text-3xl font-semibold text-zinc-950">No matches scheduled yet.</p>
            <p className="mt-3 text-sm text-zinc-500 max-w-md mx-auto">
              Fixtures are added by the admin. As soon as the next matchday is scheduled, it&apos;ll appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {matches.map((m) => (
              <MatchCard
                key={m.id}
                match={m}
                onSaved={load}
                locked={!matchDayAccess}
              />
            ))}
          </div>
        )}

        {/* Previous predictions, most recent first */}
        <section className="mt-16" data-testid="history-section">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Your history</p>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950 mt-1">
                Previous predictions.
              </h2>
            </div>
            <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">
              {history.length} {history.length === 1 ? "match" : "matches"}
            </span>
          </div>

          {historyLoading ? (
            <div className="text-sm uppercase tracking-[0.2em] text-zinc-500">Loading…</div>
          ) : history.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-sm p-10 text-center" data-testid="no-history">
              <p className="text-sm text-zinc-500">No past predictions yet — they&apos;ll show up here after today.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((row) => {
                const m = row.match;
                const actual = m.actual;
                const finished = m.status === "finished";
                return (
                  <article
                    key={m.id}
                    data-testid={`history-row-${m.id}`}
                    className="border border-zinc-200 rounded-sm bg-white"
                  >
                    <header className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                          {m.match_date} · {m.kickoff} · {finished ? "Full Time" : "Scheduled"}
                        </p>
                        <h3 className="font-heading text-lg sm:text-xl font-bold tracking-tight text-zinc-950 mt-0.5">
                          {m.home_team} <span className="text-zinc-300">vs</span> {m.away_team}
                        </h3>
                      </div>
                      <span
                        className="inline-flex items-center justify-end min-w-[3rem] h-8 px-3 rounded-sm font-heading font-bold text-base tabular-nums bg-emerald-600 text-white"
                        data-testid={`history-row-points-${m.id}`}
                      >
                        {row.points_total ?? 0} pts
                      </span>
                    </header>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm min-w-[640px]">
                        <thead>
                          <tr className="border-b border-zinc-100">
                            <th className="text-left px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">&nbsp;</th>
                            {PREDICTION_FIELDS.map((f) => (
                              <th key={f.key} className="text-center px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{f.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-zinc-100">
                            <td className="px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-zinc-950">Prediction</td>
                            {PREDICTION_FIELDS.map((f) => (
                              <td key={f.key} className="px-3 py-2 text-center tabular-nums text-zinc-700">{row.prediction?.[f.key] ?? "—"}</td>
                            ))}
                          </tr>
                          <tr className="border-b border-zinc-100">
                            <td className="px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Actual</td>
                            {PREDICTION_FIELDS.map((f) => (
                              <td key={f.key} className="px-3 py-2 text-center tabular-nums font-semibold text-zinc-950">{actual?.[f.key] ?? "—"}</td>
                            ))}
                          </tr>
                          <tr>
                            <td className="px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Points</td>
                            {PREDICTION_FIELDS.map((f) => {
                              const pts = row.points_breakdown?.[f.key] ?? pointsFor(row.prediction?.[f.key], actual?.[f.key], f.key);
                              const dim = actual == null;
                              return (
                                <td key={f.key} className="px-3 py-2 text-center">
                                  <PointsPill value={pts} dim={dim} />
                                </td>
                              );
                            })}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
